import { createSlice } from '@reduxjs/toolkit';
import api from 'src/api/index';
import i18n from 'src/i18n';
import { showErrorToast } from './toast';

const initialState = {
	list: [],
	total: 0,
	loading: false
};

const publicationsSlice = createSlice({
	name: 'publications',
	initialState,
	reducers: {
		setPublicationsList: (state, action) => {
			state.list = action.payload.list;
			state.total = action.payload.count;
		},
		updatePublicationItem: (state, action) => {
			const index = state.list.findIndex(item => item.id === action.payload.id);
			state.list.splice(index, 1, action.payload);
		},
		addPublicationsItem: (state, action) => {
			state.list.push(action.payload);
		},
		removePublicationsItem: (state, action) => {
			state.list = state.list.filter((item) => action.payload !== item.id);
		},
		setPublicationsLoading: (state, action) => {
			state.loading = action.payload;
		}
	},
});

export const { setPublicationsList, addPublicationsItem, 
	removePublicationsItem, updatePublicationItem, 
	setPublicationsLoading } = publicationsSlice.actions;

export const fetchPublicationsList = (payload = {}) => async (dispatch) => {

	dispatch(setPublicationsLoading(true));

	try {

		const res = await api.publicationsList(payload);
		dispatch(setPublicationsList(res));

	} catch (error) {

		console.error(error);
		dispatch(showErrorToast({
			description: i18n.t('toasts.failedGetPublications')
		}));

		throw error;
	} finally {

		dispatch(setPublicationsLoading(false));

	}
};

export const getPublicationById = (id) => ( dispatch, getState ) => {

	return new Promise(( resolve, reject ) => {

		const state = getState();

		const list = state.publications.list;

		let item = list.find(item => Number(item.id) === Number(id));

		if (item) {

			resolve(item);

		} else {

			api.publicationById(id)
				.then(res => {
					resolve(res);
					dispatch(addPublicationsItem(res));
				})
				.catch(() => {

					dispatch(showErrorToast({
						description: i18n.t('toasts.publicationNotFound', { id })
					}));

					reject();

				});
		}

	});

};

export const reloadPublication = (publicationId) => async (dispatch) => {

	try {

		const publication = await api.publicationById(publicationId);

		if (publication) {
			dispatch(updatePublicationItem(publication));
		}

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedUpdatePublication')
		}));

		throw error;

	}

};

export const editPublication = ({ publicationId, payload }) => async (dispatch) => {

	dispatch(setPublicationsLoading(true));

	try {
		
		await api.editPublication({ publicationId, payload: payload });

		dispatch(reloadPublication( publicationId ));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedChangePrice')
		}));

		throw error;

	} finally {

		dispatch(setPublicationsLoading(false));

	}

};


export const unpublishPublication = (publicationId) => async (dispatch) => {

	dispatch(setPublicationsLoading(true));

	try {
		
		await api.unpublishPublication(publicationId);

		dispatch(reloadPublication( publicationId ));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedUnpublish')
		}));

		throw error;

	} finally {

		dispatch(setPublicationsLoading(false));

	}

};

export const submitPublicationForReview = ( publicationId ) => async (dispatch) => {

	dispatch(setPublicationsLoading(true));

	try {
		
		await api.publicationRequestApproval( publicationId );

		dispatch(reloadPublication( publicationId ));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedPublish')
		}));

		throw error;

	} finally {

		dispatch(setPublicationsLoading(false));

	}
	
};

export const publishPublication = (publicationId) => async (dispatch) => {

	dispatch(setPublicationsLoading(true));

	try {
		
		await api.publishPublication(publicationId);

		dispatch(reloadPublication( publicationId ));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedPublish')
		}));

		throw error;

	} finally {

		dispatch(setPublicationsLoading(false));

	}

};

export const deleteDraftPublication = (publicationId) => async (dispatch) => {

	dispatch(setPublicationsLoading(true));

	try {
		
		await api.deletePublication(publicationId);

		dispatch(removePublicationsItem(publicationId));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedDeletePublication')
		}));

		throw error;

	} finally {

		dispatch(setPublicationsLoading(false));

	}

};

export default publicationsSlice.reducer;
